import React, { useState } from 'react';
import { Header } from '../../components/Header';
import { Footer } from '../../components/Footer';
import { SalesForm } from '../../components/SalesForm';
import { BarChart3, FileText, Shield, Users, CheckCircle, ArrowRight, Bot, Landmark } from 'lucide-react';
export function FinanceSolutionPage() {
  const [isSalesFormOpen, setIsSalesFormOpen] = useState(false);
  return <div className="flex flex-col min-h-screen w-full" data-id="element-975">
      <Header data-id="element-976" />
      <main className="flex-grow" data-id="element-977">
        {/* Hero Section */}
        <div className="bg-black text-white py-16 md:py-24" data-id="element-978">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-979">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center" data-id="element-980">
              <div data-id="element-981">
                <h1 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-982">
                  AI Solutions for Finance
                </h1>
                <p className="text-xl text-gray-300 mb-8" data-id="element-983">
                  Transform financial operations with intelligent AI agents that
                  automate processes, enhance compliance, and deliver deeper
                  insights.
                </p>
                <button onClick={() => setIsSalesFormOpen(true)} className="bg-white text-black px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors" data-id="element-984">
                  Request a Demo
                </button>
              </div>
              <div className="relative" data-id="element-985">
                <img src="https://images.unsplash.com/photo-1551260627-fd1b6daa6224?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" alt="Financial data" className="rounded-lg shadow-lg" data-id="element-986" />
                <div className="absolute -bottom-6 -right-6 bg-white p-4 rounded-lg shadow-lg" data-id="element-987">
                  <Landmark className="text-black" size={32} data-id="element-988" />
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Key Benefits */}
        <div className="py-16 md:py-24 bg-white" data-id="element-989">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-990">
            <div className="text-center mb-16" data-id="element-991">
              <h2 className="text-3xl font-bold mb-4" data-id="element-992">
                Key Benefits for Financial Institutions
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-993">
                Our AI agents address the complex challenges faced by banks,
                investment firms, and financial service providers.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-994">
              <BenefitCard icon={<FileText size={24} data-id="element-996" />} title="Document Processing" description="Automate the extraction and processing of financial documents, statements, and applications with high accuracy." data-id="element-995" />
              <BenefitCard icon={<Shield size={24} data-id="element-998" />} title="Compliance Automation" description="Ensure regulatory compliance with automated checks, documentation, and audit trails." data-id="element-997" />
              <BenefitCard icon={<BarChart3 size={24} data-id="element-1000" />} title="Financial Analysis" description="Generate insights from financial data to identify trends, opportunities, and potential risks." data-id="element-999" />
              <BenefitCard icon={<Users size={24} data-id="element-1002" />} title="Client Communication" description="Provide personalized client service with automated updates, alerts, and responses to common inquiries." data-id="element-1001" />
              <BenefitCard icon={<Bot size={24} data-id="element-1004" />} title="Transaction Monitoring" description="Detect unusual patterns and potential fraud with AI-powered transaction analysis." data-id="element-1003" />
              <BenefitCard icon={<CheckCircle size={24} data-id="element-1006" />} title="Process Optimization" description="Streamline workflows and reduce manual tasks to improve operational efficiency and reduce costs." data-id="element-1005" />
            </div>
          </div>
        </div>
        {/* Features */}
        <div className="py-16 md:py-24 bg-white" data-id="element-1007">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1008">
            <div className="text-center mb-16" data-id="element-1009">
              <h2 className="text-3xl font-bold mb-4" data-id="element-1010">
                Key Features for Financial Institutions
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-1011">
                Our AI agents offer specialized capabilities designed for the
                unique needs of the financial sector.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1012">
              <FeatureCard title="Intelligent Document Processing" description="Extract and validate information from loan applications, financial statements, and regulatory filings with high accuracy." data-id="element-1013" />
              <FeatureCard title="KYC/AML Automation" description="Streamline Know Your Customer and Anti-Money Laundering processes with automated verification and risk assessment." data-id="element-1014" />
              <FeatureCard title="Financial Report Generation" description="Automatically compile and format financial reports for internal analysis, client communication, and regulatory submission." data-id="element-1015" />
              <FeatureCard title="Transaction Anomaly Detection" description="Identify unusual patterns and potential fraud in transaction data using advanced machine learning algorithms." data-id="element-1016" />
              <FeatureCard title="Client Portfolio Management" description="Monitor client portfolios, generate personalized insights, and automate regular updates and recommendations." data-id="element-1017" />
              <FeatureCard title="Regulatory Compliance Monitoring" description="Stay ahead of changing regulations with automated monitoring, documentation, and implementation of compliance requirements." data-id="element-1018" />
            </div>
          </div>
        </div>
        {/* How It Works */}
        <div className="py-16 md:py-24 bg-gray-50" data-id="element-1019">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1020">
            <div className="text-center mb-16" data-id="element-1021">
              <h2 className="text-3xl font-bold mb-4" data-id="element-1022">How It Works</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-1023">
                Implementing Agentter.ai in your financial institution is
                secure, compliant, and delivers rapid results.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1024">
              <StepCard number="1" title="Secure Integration" description="Connect with your existing financial systems through our SOC 2 compliant, bank-grade secure integration platform." data-id="element-1025" />
              <StepCard number="2" title="Configure Your Agents" description="Define the tasks you want to automate using natural language, with built-in compliance guardrails." data-id="element-1026" />
              <StepCard number="3" title="Deploy and Monitor" description="Launch your AI agents with comprehensive audit trails and continuous performance monitoring." data-id="element-1027" />
            </div>
          </div>
        </div>
        {/* Security & Compliance */}
        <div className="py-16 md:py-24 bg-white" data-id="element-1028">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1029">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center" data-id="element-1030">
              <div data-id="element-1031">
                <h2 className="text-3xl font-bold mb-6" data-id="element-1032">
                  Security & Compliance
                </h2>
                <p className="text-lg text-gray-600 mb-6" data-id="element-1033">
                  We understand that security and compliance are paramount in
                  the financial sector. Agentter.ai is built from the ground up
                  with enterprise-grade security and regulatory compliance in
                  mind.
                </p>
                <div className="space-y-4 mb-6" data-id="element-1034">
                  <SecurityItem text="SOC 2 Type II certified infrastructure and processes" data-id="element-1035" />
                  <SecurityItem text="Bank-grade encryption for data at rest and in transit" data-id="element-1036" />
                  <SecurityItem text="Comprehensive audit trails for all AI agent actions" data-id="element-1037" />
                  <SecurityItem text="Role-based access controls and multi-factor authentication" data-id="element-1038" />
                  <SecurityItem text="Regular penetration testing and security assessments" data-id="element-1039" />
                  <SecurityItem text="Compliance with GDPR, CCPA, and financial regulations" data-id="element-1040" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-1041">
                <div className="bg-gray-50 p-6 rounded-lg flex items-center justify-center" data-id="element-1042">
                  <span className="font-bold text-xl" data-id="element-1043">SOC 2</span>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg flex items-center justify-center" data-id="element-1044">
                  <span className="font-bold text-xl" data-id="element-1045">GDPR</span>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg flex items-center justify-center" data-id="element-1046">
                  <span className="font-bold text-xl" data-id="element-1047">CCPA</span>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg flex items-center justify-center" data-id="element-1048">
                  <span className="font-bold text-xl" data-id="element-1049">ISO 27001</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* CTA Section */}
        <div className="bg-black text-white py-16" data-id="element-1050">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1051">
            <h2 className="text-3xl font-bold mb-6" data-id="element-1052">
              Ready to transform your financial operations?
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto" data-id="element-1053">
              Schedule a personalized demo to see how Agentter.ai can automate
              and optimize your financial workflows while maintaining the
              highest security standards.
            </p>
            <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4" data-id="element-1054">
              <button onClick={() => setIsSalesFormOpen(true)} className="bg-white text-black px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors" data-id="element-1055">
                Request a Demo
              </button>
              <a href="#" className="border border-white text-white px-6 py-3 rounded-lg font-medium hover:bg-gray-900 transition-colors" data-id="element-1056">
                View Pricing
              </a>
            </div>
          </div>
        </div>
      </main>
      <Footer data-id="element-1057" />
      {/* Sales Form Modal */}
      <SalesForm isOpen={isSalesFormOpen} onClose={() => setIsSalesFormOpen(false)} agentType="Finance Solutions" data-id="element-1058" />
    </div>;
}
function BenefitCard({
  icon,
  title,
  description
}) {
  return <div className="bg-gray-50 p-6 rounded-lg border border-gray-100 hover:shadow-md transition-shadow" data-id="element-1059">
      <div className="bg-black text-white p-3 rounded-lg inline-block mb-4" data-id="element-1060">
        {icon}
      </div>
      <h3 className="text-xl font-semibold mb-2" data-id="element-1061">{title}</h3>
      <p className="text-gray-600" data-id="element-1062">{description}</p>
    </div>;
}
function StepCard({
  number,
  title,
  description
}) {
  return <div className="bg-white p-6 rounded-lg border border-gray-100 hover:shadow-md transition-shadow" data-id="element-1063">
      <div className="bg-black text-white w-10 h-10 rounded-full flex items-center justify-center mb-4 font-bold" data-id="element-1064">
        {number}
      </div>
      <h3 className="text-xl font-semibold mb-2" data-id="element-1065">{title}</h3>
      <p className="text-gray-600" data-id="element-1066">{description}</p>
    </div>;
}
function ResultItem({
  text
}) {
  return <div className="flex items-start" data-id="element-1067">
      <CheckCircle className="text-green-500 mr-2 mt-1 flex-shrink-0" size={20} data-id="element-1068" />
      <span data-id="element-1069">{text}</span>
    </div>;
}
function SecurityItem({
  text
}) {
  return <div className="flex items-start" data-id="element-1070">
      <Shield className="text-black mr-2 mt-1 flex-shrink-0" size={20} data-id="element-1071" />
      <span data-id="element-1072">{text}</span>
    </div>;
}
function FeatureCard({
  title,
  description
}) {
  return <div className="bg-white p-6 rounded-lg border border-gray-100 hover:shadow-md transition-shadow" data-id="element-1073">
      <h3 className="text-xl font-semibold mb-3" data-id="element-1074">{title}</h3>
      <p className="text-gray-600" data-id="element-1075">{description}</p>
    </div>;
}